import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useFocusTrap } from '../utils/focusTrap';
import { createPreviewShare } from '../utils/createPreviewShare';
import { CopyButton } from './shared';
import { logger } from '../log';
import styles from './ShareVersionDialog.module.css';

const log = logger('share-version');

interface ShareVersionDialogProps {
  isOpen: boolean;
  versionId: string;
  versionName: string;
  appId: string;
  projectId: string;
  onClose: () => void;
}

type ShareState = 'config' | 'creating' | 'ready' | 'error';

export const ShareVersionDialog: React.FC<ShareVersionDialogProps> = ({
  isOpen,
  versionId,
  versionName,
  appId,
  projectId,
  onClose,
}) => {
  const { user } = useUser();
  const modalRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ShareState>('config');
  const [description, setDescription] = useState<string>('');
  const [expiryMinutes, setExpiryMinutes] = useState<number>(60); // 1 hour default
  const [passcode, setPasscode] = useState<string>('');
  const [shareId, setShareId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);

  // Advanced stream configuration
  const [pointerLock, setPointerLock] = useState(false);
  const [nativeTouch, setNativeTouch] = useState(false);
  const [resolutionClamp, setResolutionClamp] = useState<string>('');
  const [forceRelay, setForceRelay] = useState(false);
  const [suppressBrowserKeys, setSuppressBrowserKeys] = useState(false);
  const [useMic, setUseMic] = useState(false);

  // Enable focus trap
  useFocusTrap(modalRef, isOpen);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setState('config');
      setDescription('');
      setExpiryMinutes(60);
      setPasscode('');
      setShareId('');
      setError('');
      setCopied(false);
      setPointerLock(false);
      setNativeTouch(false);
      setResolutionClamp('');
      setForceRelay(false);
      setSuppressBrowserKeys(false);
      setUseMic(false);
    }
  }, [isOpen]);

  // Handle keyboard events
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleCreateShare = useCallback(async () => {
    setState('creating');
    setError('');

    try {
      // Build streamConfig from advanced options
      const streamConfig: Record<string, unknown> = {};
      if (pointerLock) streamConfig.pointerLock = true;
      if (nativeTouch) streamConfig.nativeTouch = true;
      if (resolutionClamp) streamConfig.resolutionClamp = resolutionClamp;
      if (forceRelay) streamConfig.forceRelay = true;
      if (suppressBrowserKeys) streamConfig.suppressBrowserKeys = true;
      if (useMic) streamConfig.useMic = true;

      const { id } = await createPreviewShare({
        projectId,
        appId,
        versionId,
        expiresInMinutes: expiryMinutes,
        passcode: passcode || undefined,
        streamConfig: Object.keys(streamConfig).length > 0 ? streamConfig : undefined,
      });

      setShareId(id);
      setState('ready');
    } catch (err: any) {
      log.error('failed to create share', err);
      setError(err.message || 'Failed to create share link');
      setState('error');
    }
  }, [projectId, appId, versionId, expiryMinutes, passcode, pointerLock, nativeTouch, resolutionClamp, forceRelay, suppressBrowserKeys, useMic]);

  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/preview/${shareId}`;
  };

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      log.error('failed to copy', err);
    }
  }, [shareId]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="share-dialog-title" className={styles.modalTitle}>
          Share Version
        </h2>

        <div className={styles.versionInfo}>
          <span className={styles.versionLabel}>Version:</span>
          <span className={styles.versionValue}>{versionName}</span>
        </div>

        {state === 'config' && (
          <>
            {user?.primaryEmailAddress?.emailAddress && (
              <div className={styles.createdByInfo}>
                <span className={styles.createdByLabel}>Created by:</span>
                <span className={styles.createdByValue}>
                  {user.primaryEmailAddress.emailAddress}
                </span>
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="description-input" className={styles.label}>
                What is this link for?
              </label>
              <input
                id="description-input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Client review, stakeholder demo"
                className={styles.input}
                autoComplete="off"
              />
              <span className={styles.hint}>
                Optional description to help you remember the purpose of this share link
              </span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="expiry-select" className={styles.label}>
                Link expires in
              </label>
              <select
                id="expiry-select"
                value={expiryMinutes}
                onChange={(e) => setExpiryMinutes(Number(e.target.value))}
                className={styles.select}
              >
                <option value={60}>1 hour</option>
                <option value={1440}>24 hours</option>
                <option value={10080}>7 days</option>
                <option value={43200}>30 days</option>
                <option value={0}>Never</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="passcode-input" className={styles.label}>
                Passcode (optional)
              </label>
              <input
                id="passcode-input"
                type="text"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Leave empty for no passcode"
                className={styles.input}
                autoComplete="off"
              />
              <span className={styles.hint}>
                If set, viewers must enter this passcode to access the preview
              </span>
            </div>

            <details className={styles.advancedSection}>
              <summary className={styles.advancedSummary}>Advanced stream settings</summary>
              <div className={styles.advancedContent}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={pointerLock}
                    onChange={(e) => setPointerLock(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Pointer lock</span>
                  <span className={styles.hint}>Captures cursor for FPS-style 3D navigation</span>
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={nativeTouch}
                    onChange={(e) => setNativeTouch(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Native multi-touch</span>
                  <span className={styles.hint}>Real touch events instead of mouse emulation</span>
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={suppressBrowserKeys}
                    onChange={(e) => setSuppressBrowserKeys(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Suppress browser keys</span>
                  <span className={styles.hint}>Prevent browser shortcuts (F5, Ctrl+T, etc.)</span>
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={forceRelay}
                    onChange={(e) => setForceRelay(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Force relay</span>
                  <span className={styles.hint}>Route through TURN relay (higher latency, better firewall compatibility)</span>
                </label>

                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={useMic}
                    onChange={(e) => setUseMic(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Microphone</span>
                  <span className={styles.hint}>Enable microphone input (requires secure context)</span>
                </label>

                <div className={styles.formGroup}>
                  <label htmlFor="resolution-clamp-select" className={styles.label}>
                    Resolution clamp
                  </label>
                  <select
                    id="resolution-clamp-select"
                    value={resolutionClamp}
                    onChange={(e) => setResolutionClamp(e.target.value)}
                    className={styles.select}
                  >
                    <option value="">None (native resolution)</option>
                    <option value="sxga">SXGA (1280x1024)</option>
                    <option value="hd">HD (1366x768)</option>
                    <option value="hdplus">HD+ (1600x900)</option>
                    <option value="fhd">FHD (1920x1080)</option>
                    <option value="wuxga">WUXGA (1920x1200)</option>
                    <option value="qhd">QHD (2560x1440)</option>
                    <option value="wqhd">WQHD (3440x1440)</option>
                    <option value="uhd">UHD (3840x2160)</option>
                  </select>
                  <span className={styles.hint}>Cap the stream resolution sent to the server</span>
                </div>
              </div>
            </details>

            <div className={styles.buttonGroup}>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateShare}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Create Link
              </button>
            </div>
          </>
        )}

        {state === 'creating' && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Creating share link...</span>
          </div>
        )}

        {state === 'ready' && (
          <>
            <div className={styles.successState}>
              <div className={styles.linkContainer}>
                <input
                  type="text"
                  value={getShareUrl()}
                  readOnly
                  className={styles.linkInput}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={handleCopyLink}
                  className={`${styles.button} ${styles.buttonCopy}`}
                  title="Copy link"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {passcode && (
                <div className={styles.passcodeInfo}>
                  <span className={styles.passcodeLabel}>Passcode:</span>
                  <span className={styles.passcodeValue}>{passcode}</span>
                  <CopyButton value={passcode} aria-label="Copy passcode" />
                </div>
              )}
              <div className={styles.expiryInfo}>
                {expiryMinutes === 0
                  ? 'Never expires'
                  : expiryMinutes < 60
                    ? `Expires in ${expiryMinutes} minutes`
                    : expiryMinutes === 60
                      ? 'Expires in 1 hour'
                      : expiryMinutes === 1440
                        ? 'Expires in 24 hours'
                        : expiryMinutes === 10080
                          ? 'Expires in 7 days'
                          : expiryMinutes === 43200
                            ? 'Expires in 30 days'
                            : `Expires in ${Math.floor(expiryMinutes / 60)} hours`}
              </div>
            </div>

            <div className={styles.buttonGroup}>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Done
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <div className={styles.errorState}>
              <span className={styles.errorIcon}>!</span>
              <span className={styles.errorMessage}>{error}</span>
            </div>

            <div className={styles.buttonGroup}>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={() => setState('config')}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
